import React, { ChangeEvent, FC, useState } from 'react'
import classNamesBind from 'classnames/bind'
import css from './styles.module.scss'
import Image from 'next/image'
import snowflakeImage from '/public/static/share/images/small_snowflake.svg'
import ColorIcon from '@dashboard/components/ColorIcon'
import Input from '@dashboard/components/Input'
import Button from '@dashboard/components/Button'
import { KeyedStateHelper, USER_ACTIONS } from '@dashboard/lib/types'
import { validate as validateEmail } from 'email-validator'
import { useTimeouts } from '@dashboard/hooks/useTimeouts'
import { HSSubmitFormData } from '@dashboard/lib/analytics'
import { useAppDispatch } from '@dashboard/hooks/useAppDispatch'
import { createUserAction } from '@dashboard/store/actions/user/action/create'
import { useAppStateSelector } from '@dashboard/hooks/useAppStateSelector'
import { useOnSuccess } from '@dashboard/hooks/useOnSuccess'
import { loadMe } from '@dashboard/store/actions/user/me'

interface OwnProps {
  active: boolean
  onClose: () => void
}

const classNames = classNamesBind.bind(css)

const ProductSurveyRecruitment: FC<OwnProps> = ({
  active,
  onClose,
}: OwnProps) => {
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [errors, setErrors] = useState<KeyedStateHelper<string>>({})
  const [success, setSuccess] = useState(false)
  const timeouts = useTimeouts()
  const dispatch = useAppDispatch()
  const { createActionState } = useAppStateSelector(state => state.user)

  // Functions
  const handleClose = () => {
    dispatch(createUserAction(USER_ACTIONS.closed_product_survey_recruitment))
    onClose()
  }

  const handleSubmit = () => {
    const newErrors: KeyedStateHelper<string> = {}

    if (!firstName) newErrors.firstName = 'First name required'
    if (!lastName) newErrors.lastName = 'Last name required'

    if (!email) newErrors.email = 'Email required'
    else if (!validateEmail(email)) newErrors.email = 'Must be a valid email'

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
    } else {
      HSSubmitFormData(
        '1716276',
        'fe6f67c7-6633-4650-a09a-068777af2f2c',
        email,
        [
          { name: 'firstname', value: firstName },
          { name: 'lastname', value: lastName },
        ],
      )
      setSuccess(true)
      timeouts.push(window.setTimeout(handleClose, 5000))
    }
  }

  const updateFormField = (callback: (val: string) => void) => {
    return (e: ChangeEvent<HTMLInputElement>) => {
      if (Object.keys(errors).length > 0) setErrors({})

      callback(e.target.value)
    }
  }

  // Effects
  useOnSuccess([createActionState], () => {
    dispatch(loadMe())
  })

  return (
    <div className={classNames('container', { active })}>
      <div className={classNames('header')}>
        <div className={classNames('image')}>
          <Image
            src={snowflakeImage}
            width={30}
            height={30}
            alt="Crystal snowflake icon"
          />
        </div>
        <div className={classNames('headerText')}>Can You Talk? 💰</div>
        <div className={classNames('close')} onClick={handleClose}>
          <ColorIcon icon="close" width={12} discType="white" />
        </div>
      </div>
      <div className={classNames('content')}>
        <div className={classNames('text')}>
          We're making improvements and need your help. Do you have 30 minutes
          to tell us about your experience?
        </div>
        <div className={classNames('bold')}>
          After talking, we'll send you a $20 Amazon gift card to say thanks.
        </div>
        <div className={classNames('form')}>
          <form onSubmit={e => e.preventDefault()}>
            <div>
              <Input
                label="First Name"
                value={firstName}
                onChange={updateFormField(setFirstName)}
                error={errors.firstName}
              />
            </div>
            <div>
              <Input
                label="Last Name"
                value={lastName}
                onChange={updateFormField(setLastName)}
                error={errors.lastName}
              />
            </div>
            <div>
              <Input
                label="Email"
                value={email}
                onChange={updateFormField(setEmail)}
                error={errors.email}
              />
            </div>
            <div className={classNames('buttonContainer')}>
              <Button buttonTag onClick={handleSubmit} success={success}>
                Submit
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default ProductSurveyRecruitment
